'use client'

import React from 'react'
import {
  Search,
  Quote,
  MessageCircle,
  FileCheck,
  ArrowDown,
  ArrowRight,
  Clock,
  CalendarDays,
  CalendarRange,
  Gauge,
  Bot,
  ScanSearch,
  SearchCheck,
  FileCode,
  Youtube,
} from 'lucide-react'
import { GRADE_RANGE } from '@/lib/radar/scoring'

/**
 * AI Visibility Tools Diagrams
 * Visual components for the Free AI Visibility Tools Complete Guide
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

const TOOL_COLORS = {
  crawl: '#008dcc',
  citation: '#F90B8A',
  reddit: '#F97316',
  validator: '#10b981',
  readiness: '#8b5cf6',
  robots: '#20808d',
} as const

/**
 * Toolkit Workflow: Shows how the 4 tools connect in a process flow
 */
export function AIVisibilityToolkitWorkflow() {
  const steps = [
    {
      phase: 'Start Here',
      tool: 'AI Crawl Checker',
      question: 'Can AI bots access your site?',
      color: TOOL_COLORS.crawl,
      icon: Search,
      link: '/tools/ai-crawl-checker',
    },
    {
      phase: 'Deep Dive',
      tool: 'robots.txt Analyzer',
      question: 'Are your bot directives optimized?',
      color: TOOL_COLORS.robots,
      icon: Bot,
      link: '/tools/robots-txt-analyzer',
    },
    {
      phase: 'Measure Visibility',
      tool: 'AI Citation Tracker',
      question: 'Do AI engines cite you?',
      color: TOOL_COLORS.citation,
      icon: Quote,
      link: '/tools/ai-citation-tracker',
    },
    {
      phase: 'AI Discovery File',
      tool: 'llms.txt Validator',
      question: 'Is your AI file ready?',
      color: TOOL_COLORS.validator,
      icon: FileCheck,
      link: '/tools/llms-txt-validator',
    },
    {
      phase: 'Ongoing Monitoring',
      tool: 'Reddit Brand Monitor',
      question: 'What does the community say?',
      color: TOOL_COLORS.reddit,
      icon: MessageCircle,
      link: '/tools/reddit-brand-monitor',
    },
    {
      phase: 'Unified Score',
      tool: 'AI Readiness Score',
      question: 'What is your overall AI readiness?',
      color: TOOL_COLORS.readiness,
      icon: Gauge,
      link: '/tools/ai-readiness-score',
    },
  ]

  const useCases = [
    {
      title: 'New Site Launch',
      description:
        'Run Crawl Checker + robots.txt Analyzer first, then Validator, then set up Citation Tracker monthly',
      color: TOOL_COLORS.crawl,
    },
    {
      title: 'Monthly Monitoring',
      description:
        'Citation Tracker + Reddit Monitor together for a full visibility pulse check, AI Readiness Score for the unified view',
      color: TOOL_COLORS.citation,
    },
    {
      title: 'GEO Optimization',
      description:
        'All 13 tools in sequence to identify gaps, fix them, and measure progress',
      color: TOOL_COLORS.validator,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <h3 className='text-lg font-semibold text-foreground mb-1'>
        How the 9 Tools Work Together
      </h3>
      <p className='text-sm text-muted-foreground mb-6'>
        A recommended workflow from first audit to ongoing monitoring
      </p>

      {/* Workflow Steps */}
      <div className='flex flex-col gap-3'>
        {steps.map((step, i) => (
          <React.Fragment key={step.tool}>
            <div className='bg-background border border-border/60 rounded-xl p-4'>
              <div className='flex items-start gap-4'>
                <div
                  className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${step.color}15` }}
                >
                  <step.icon
                    className='w-5 h-5'
                    style={{ color: step.color }}
                  />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span
                      className='text-xs font-medium px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${step.color}15`,
                        color: step.color,
                      }}
                    >
                      {step.phase}
                    </span>
                  </div>
                  <p className='font-medium text-foreground'>{step.tool}</p>
                  <p className='text-sm text-foreground/80'>{step.question}</p>
                </div>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className='flex justify-center'>
                <ArrowDown className='w-4 h-4 text-muted-foreground' />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Use Cases */}
      <div className='mt-8'>
        <h4 className='text-sm font-semibold text-foreground mb-3'>
          Common Workflows
        </h4>
        <div className='grid md:grid-cols-3 gap-3'>
          {useCases.map(uc => (
            <div
              key={uc.title}
              className='rounded-lg p-3'
              style={{
                backgroundColor: `${uc.color}08`,
                border: `1px solid ${uc.color}20`,
              }}
            >
              <p
                className='text-sm font-medium mb-1'
                style={{ color: uc.color }}
              >
                {uc.title}
              </p>
              <p className='text-xs text-foreground/80'>{uc.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Score Comparison: Side-by-side comparison of all 4 tools
 */
export function AIVisibilityScoreComparison() {
  const tools = [
    {
      name: 'AI Crawl Checker',
      color: TOOL_COLORS.crawl,
      icon: Search,
      bestFor: 'Technical AI readiness audit',
      categories: [
        { name: 'Bot Access & robots.txt', weight: '40 pts' },
        { name: 'Structured Data', weight: '25 pts' },
        { name: 'llms.txt File', weight: '20 pts' },
        { name: 'Content Quality', weight: '15 pts' },
      ],
    },
    {
      name: 'AI Citation Tracker',
      color: TOOL_COLORS.citation,
      icon: Quote,
      bestFor: 'AI search engine visibility',
      categories: [
        { name: 'Competitive Visibility', weight: '35 pts' },
        { name: 'Brand Recognition', weight: '25 pts' },
        { name: 'URL Citations', weight: '20 pts' },
        { name: 'Sentiment + Consistency', weight: '20 pts' },
      ],
    },
    {
      name: 'Reddit Brand Monitor',
      color: TOOL_COLORS.reddit,
      icon: MessageCircle,
      bestFor: 'Community reputation & seeding detection',
      categories: [
        { name: 'Mention Volume', weight: '25 pts' },
        { name: 'Subreddit Diversity', weight: '20 pts' },
        { name: 'Sentiment Score', weight: '20 pts' },
        { name: 'Organic vs Seeded', weight: '20 pts' },
      ],
    },
    {
      name: 'llms.txt Validator',
      color: TOOL_COLORS.validator,
      icon: FileCheck,
      bestFor: 'AI discovery file compliance',
      categories: [
        { name: 'Structure', weight: '20 pts' },
        { name: 'Content Sections', weight: '20 pts' },
        { name: 'Links & URLs', weight: '20 pts' },
        { name: 'Entity Definitions + Policy', weight: '25 pts' },
      ],
    },
    {
      name: 'AI Readiness Score',
      color: TOOL_COLORS.readiness,
      icon: Gauge,
      bestFor: 'Unified AI readiness measurement',
      categories: [
        { name: 'Bot Discoverability', weight: '30 pts' },
        { name: 'Structured Data', weight: '25 pts' },
        { name: 'LLM Communication', weight: '25 pts' },
        { name: 'Content + Cross-Signal', weight: '20 pts' },
      ],
    },
    {
      name: 'robots.txt Analyzer',
      color: TOOL_COLORS.robots,
      icon: Bot,
      bestFor: 'Deep robots.txt analysis for AI bots',
      categories: [
        { name: 'AI Bot Coverage', weight: '30 pts' },
        { name: 'Search Bot Coverage', weight: '20 pts' },
        { name: 'File Structure + Policy', weight: '40 pts' },
        { name: 'Best Practices', weight: '10 pts' },
      ],
    },
  ]

  const grades = [
    { grade: 'A', range: GRADE_RANGE.A, label: 'Excellent', color: '#22c55e' },
    { grade: 'B', range: GRADE_RANGE.B, label: 'Good', color: '#008dcc' },
    { grade: 'C', range: GRADE_RANGE.C, label: 'Needs Work', color: '#FDC304' },
    { grade: 'D', range: GRADE_RANGE.D, label: 'Poor', color: '#f97316' },
    { grade: 'F', range: GRADE_RANGE.F, label: 'Critical', color: '#ef4444' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <h3 className='text-lg font-semibold text-foreground mb-1'>
        Score Breakdown: All 9 Tools Compared
      </h3>
      <p className='text-sm text-muted-foreground mb-6'>
        Each tool scores 0-100 across different dimensions
      </p>

      {/* Tool Cards */}
      <div className='grid md:grid-cols-2 gap-4'>
        {tools.map(tool => (
          <div
            key={tool.name}
            className='bg-background border border-border/60 rounded-xl p-4'
          >
            <div className='flex items-center gap-3 mb-3'>
              <div
                className='w-8 h-8 rounded-lg flex items-center justify-center'
                style={{ backgroundColor: `${tool.color}15` }}
              >
                <tool.icon className='w-4 h-4' style={{ color: tool.color }} />
              </div>
              <div>
                <p className='font-medium text-foreground text-sm'>
                  {tool.name}
                </p>
                <p className='text-xs text-muted-foreground'>{tool.bestFor}</p>
              </div>
            </div>
            <div className='space-y-2'>
              {tool.categories.map(cat => (
                <div
                  key={cat.name}
                  className='flex items-center justify-between'
                >
                  <span className='text-xs text-foreground/80'>{cat.name}</span>
                  <span
                    className='text-xs font-medium px-2 py-0.5 rounded'
                    style={{
                      backgroundColor: `${tool.color}10`,
                      color: tool.color,
                    }}
                  >
                    {cat.weight}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Grade Scale */}
      <div className='mt-6'>
        <h4 className='text-sm font-semibold text-foreground mb-3'>
          Grade Scale (all tools)
        </h4>
        <div className='flex flex-wrap gap-2'>
          {grades.map(g => (
            <div
              key={g.grade}
              className='flex items-center gap-2 rounded-lg px-3 py-1.5'
              style={{
                backgroundColor: `${g.color}10`,
                border: `1px solid ${g.color}20`,
              }}
            >
              <span className='text-sm font-bold' style={{ color: g.color }}>
                {g.grade}
              </span>
              <span className='text-xs text-foreground/80'>
                {g.range} ({g.label})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Frequency Guide: Recommended check frequency for each tool
 */
export function AIVisibilityFrequencyGuide() {
  const frequencies = [
    {
      period: 'Weekly',
      icon: Clock,
      items: [
        {
          tool: 'Citation Tracker',
          detail: 'Competitive queries only',
          color: TOOL_COLORS.citation,
        },
      ],
    },
    {
      period: 'Monthly',
      icon: CalendarDays,
      items: [
        {
          tool: 'Citation Tracker',
          detail: 'Full scan (all query types)',
          color: TOOL_COLORS.citation,
        },
        {
          tool: 'Reddit Brand Monitor',
          detail: 'Brand reputation check',
          color: TOOL_COLORS.reddit,
        },
      ],
    },
    {
      period: 'Quarterly',
      icon: CalendarRange,
      items: [
        {
          tool: 'AI Crawl Checker',
          detail: 'After robots.txt or site changes',
          color: TOOL_COLORS.crawl,
        },
        {
          tool: 'robots.txt Analyzer',
          detail: 'After robots.txt policy changes',
          color: TOOL_COLORS.robots,
        },
        {
          tool: 'llms.txt Validator',
          detail: 'After content or entity updates',
          color: TOOL_COLORS.validator,
        },
        {
          tool: 'AI Readiness Score',
          detail: 'Full unified readiness check',
          color: TOOL_COLORS.readiness,
        },
      ],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <h3 className='text-lg font-semibold text-foreground mb-1'>
        Recommended Check Frequency
      </h3>
      <p className='text-sm text-muted-foreground mb-6'>
        How often to run each tool for effective AI visibility monitoring
      </p>

      <div className='space-y-4'>
        {frequencies.map(freq => (
          <div
            key={freq.period}
            className='bg-background border border-border/60 rounded-xl p-4'
          >
            <div className='flex items-center gap-2 mb-3'>
              <freq.icon className='w-4 h-4 text-foreground/80' />
              <span className='font-medium text-foreground'>{freq.period}</span>
            </div>
            <div className='space-y-2'>
              {freq.items.map(item => (
                <div
                  key={`${item.tool}-${item.detail}`}
                  className='flex items-center gap-3'
                >
                  <div
                    className='w-2 h-2 rounded-full flex-shrink-0'
                    style={{ backgroundColor: item.color }}
                  />
                  <div className='flex-1 flex items-center justify-between'>
                    <span
                      className='text-sm font-medium'
                      style={{ color: item.color }}
                    >
                      {item.tool}
                    </span>
                    <span className='text-xs text-muted-foreground'>
                      {item.detail}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Priority Note */}
      <div
        className='mt-4 rounded-lg p-3'
        style={{
          backgroundColor: `${TOOL_COLORS.crawl}08`,
          border: `1px solid ${TOOL_COLORS.crawl}20`,
        }}
      >
        <p className='text-xs text-foreground/80'>
          <span className='font-medium text-foreground'>Priority tip:</span>{' '}
          Always run the Crawl Checker first. If bots cannot access your site,
          fixing citations or llms.txt will not help.
        </p>
      </div>
    </div>
  )
}

/**
 * Tool CTA Card: Gradient card linking to a specific tool
 */
const TOOL_CONFIG = {
  'ai-crawl-checker': {
    label: 'AI Crawl Checker',
    tagline: 'Can AI Bots Find Your Content?',
    description:
      'Test how GPTBot, Claude, Perplexity, and 11 other bots see your website. Checks robots.txt, structured data, llms.txt, and content accessibility.',
    href: '/tools/ai-crawl-checker',
    cta: 'Try the AI Crawl Checker',
    icon: Search,
    gradient: 'linear-gradient(135deg, #008dcc, #0ea5e9)',
    accent: '#008dcc',
  },
  'ai-citation-tracker': {
    label: 'AI Citation Tracker',
    tagline: 'Do AI Search Engines Know You Exist?',
    description:
      'Query ChatGPT, Perplexity, Claude, and Gemini simultaneously. Measure brand mentions, URL citations, sentiment, and competitive visibility.',
    href: '/tools/ai-citation-tracker',
    cta: 'Try the AI Citation Tracker',
    icon: Quote,
    gradient: 'linear-gradient(135deg, #F90B8A, #ec4899)',
    accent: '#F90B8A',
    paid: true,
  },
  'reddit-brand-monitor': {
    label: 'Reddit Brand Monitor',
    tagline: 'What Is Reddit Saying About Your Brand?',
    description:
      'Discover brand mentions on Reddit with two-layer seeding detection. Spot astroturfing, analyze sentiment, and monitor your reputation in LLM training data.',
    href: '/tools/reddit-brand-monitor',
    cta: 'Try the Reddit Brand Monitor',
    icon: MessageCircle,
    gradient: 'linear-gradient(135deg, #F97316, #fb923c)',
    accent: '#F97316',
  },
  'llms-txt-validator': {
    label: 'llms.txt Validator',
    tagline: 'Is Your AI Discovery File Ready?',
    description:
      'Deep validation of your llms.txt against the emerging spec. Scores structure, content, links, entity definitions, use policy, and completeness.',
    href: '/tools/llms-txt-validator',
    cta: 'Try the llms.txt Validator',
    icon: FileCheck,
    gradient: 'linear-gradient(135deg, #10b981, #34d399)',
    accent: '#10b981',
  },
  'ai-readiness-score': {
    label: 'AI Readiness Score',
    tagline: 'How AI-Ready Is Your Website?',
    description:
      'One unified score combining bot access, structured data, llms.txt quality, content accessibility, and cross-signal readiness into a single 0-100 report.',
    href: '/tools/ai-readiness-score',
    cta: 'Try the AI Readiness Score',
    icon: Gauge,
    gradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    accent: '#8b5cf6',
  },
  'robots-txt-analyzer': {
    label: 'robots.txt Analyzer',
    tagline: 'Are Your Bot Directives Optimized?',
    description:
      'Deep analysis of your robots.txt for AI bots. Per-bot rule matching for 16 bots, syntax validation, policy clarity scoring, and suggested additions.',
    href: '/tools/robots-txt-analyzer',
    cta: 'Try the robots.txt Analyzer',
    icon: Bot,
    gradient: 'linear-gradient(135deg, #20808d, #2ba3b3)',
    accent: '#20808d',
  },
  'aeo-page-auditor': {
    label: 'AEO Page Auditor',
    tagline: 'Is Your Page Answer Engine Ready?',
    description:
      'Score any page for answer engine readiness. Checks speakable schema, answer-first structure, structured data quality, data extractability, content freshness, and entity authority.',
    href: '/tools/aeo-page-auditor',
    cta: 'Try the AEO Page Auditor',
    icon: ScanSearch,
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    accent: '#7c3aed',
    paid: true,
  },
  'answer-engine-tester': {
    label: 'Answer Engine Citation Tester',
    tagline: 'Do AI Engines Cite Your Page?',
    description:
      'Test if ChatGPT, Perplexity, Claude, and Gemini cite your specific page for a question. Identifies content gaps and competitor pages being cited instead.',
    href: '/tools/answer-engine-tester',
    cta: 'Try the Citation Tester',
    icon: SearchCheck,
    gradient: 'linear-gradient(135deg, #dc2626, #ef4444)',
    accent: '#dc2626',
  },
  'llms-txt-generator': {
    label: 'llms.txt Generator',
    tagline: 'Need an llms.txt? Generate One in Seconds',
    description:
      'Paste a URL and generate a structured llms.txt shaped to the Pixelmojo standard, built from your real page signals. Free, deterministic, copy or download.',
    href: '/tools/llms-txt-generator',
    cta: 'Try the llms.txt Generator',
    icon: FileCode,
    gradient: 'linear-gradient(135deg, #06b6d4, #008dcc)',
    accent: '#06b6d4',
  },
  'youtube-brand-monitor': {
    label: 'YouTube Brand Monitor',
    tagline: 'What Is YouTube Saying About Your Brand?',
    description:
      'Track brand mentions across YouTube videos. Scores mention volume, channel diversity, total reach, sentiment, and recency, where AI engines increasingly pull citations and context.',
    href: '/tools/youtube-brand-monitor',
    cta: 'Try the YouTube Brand Monitor',
    icon: Youtube,
    gradient: 'linear-gradient(135deg, #ef4444, #f87171)',
    accent: '#ef4444',
  },
} as const

type ToolKey = keyof typeof TOOL_CONFIG

export function ToolCTACard({ tool }: { tool: ToolKey }) {
  const config = TOOL_CONFIG[tool]
  if (!config) return null
  const Icon = config.icon
  const paid = 'paid' in config && config.paid

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <a
        href={config.href}
        className='block !no-underline !decoration-transparent group'
      >
        <div
          className='relative overflow-hidden rounded-2xl p-8 md:p-10 transition-transform duration-200 hover:scale-[1.01]'
          style={{ background: config.gradient }}
        >
          <div className='flex items-center gap-8'>
            <div className='flex-1 min-w-0'>
              <span className='inline-block text-[11px] font-bold tracking-[1.5px] uppercase text-white/80 mb-3'>
                {paid ? 'Paid · $5' : 'Free Tool'}
              </span>
              <h3 className='text-xl md:text-2xl font-extrabold text-white leading-tight mt-0 mb-3'>
                {config.tagline}
              </h3>
              <p className='text-sm md:text-[15px] text-white/85 leading-relaxed mb-5 mt-0'>
                {config.description}
              </p>
              <span
                className='inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-lg font-semibold text-sm group-hover:bg-white/95 transition-colors'
                style={{ color: config.accent }}
              >
                {config.cta}
                <ArrowRight size={16} />
              </span>
            </div>
            <div className='hidden md:flex items-center justify-center w-28 h-28 bg-white/15 rounded-2xl flex-shrink-0'>
              <Icon size={48} className='text-white/90' />
            </div>
          </div>
        </div>
      </a>
    </div>
  )
}
